/**
 * useShopSeo — Phase 4e.F (Feb 2026)
 *
 * React-Hook für SEO-Optimierung von Shop-Seiten ohne `react-helmet`:
 *  - setzt <title> + <meta name="description">
 *  - setzt Open-Graph + Twitter-Card-Tags (og:title, og:image, og:type)
 *  - injiziert ein <script type="application/ld+json"> mit Schema.org-Markup
 *    (LocalBusiness/Organization + ItemList aller Artikel oder Product+Offer)
 *
 * Googlebot rendert seit 2018 JavaScript und liest JSON-LD aus dem DOM.
 * Bing & Yandex auch. Beim Unmount werden injizierte Tags wieder entfernt.
 */
import { useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function upsertMeta(selector, attrs) {
    let el = document.head.querySelector(selector);
    const isNew = !el;
    if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    return { el, isNew };
}

function injectJsonLd(id, obj) {
    let el = document.getElementById(id);
    const isNew = !el;
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(obj);
    return { el, isNew };
}

function shopAbsoluteUrl(slug) {
    if (typeof window === "undefined") return `/shop/${slug}`;
    return `${window.location.origin}/shop/${slug}`;
}

export default function useShopSeo({ shop, articles, articleDetail }) {
    useEffect(() => {
        if (!shop) return undefined;

        const name = shop.company_name || shop.slug;
        const created = []; // {el, isNew, prevContent}

        // Title
        const prevTitle = document.title;
        document.title = articleDetail
            ? `${articleDetail.name} · ${name} · twowheels.info Shop`
            : `${name} · Onlineshop · twowheels.info`;

        // Description
        const description = articleDetail
            ? (articleDetail.description || `${articleDetail.name} bei ${name} – Preis auf Anfrage.`).slice(0, 160)
            : `Onlineshop von ${name}${shop.city ? ` in ${shop.city}` : ""}. Originalteile, Reifen, Zubehör – sofort lieferbar.`;
        created.push({ ...upsertMeta('meta[name="description"]', { name: "description", content: description }), key: "description", prevContent: null });

        // Open Graph
        created.push({ ...upsertMeta('meta[property="og:type"]', { property: "og:type", content: articleDetail ? "product" : "website" }), key: "og:type" });
        created.push({ ...upsertMeta('meta[property="og:title"]', { property: "og:title", content: articleDetail ? `${articleDetail.name} · ${name}` : `${name} · Shop` }), key: "og:title" });
        created.push({ ...upsertMeta('meta[property="og:description"]', { property: "og:description", content: description }), key: "og:description" });
        created.push({ ...upsertMeta('meta[property="og:url"]', { property: "og:url", content: shopAbsoluteUrl(shop.slug) }), key: "og:url" });
        const ogImage = articleDetail?.photo_paths?.[0]
            ? `${API}/api/shop/photo/${articleDetail.photo_paths[0]}`
            : shop.company_logo_url;
        if (ogImage) {
            created.push({ ...upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage }), key: "og:image" });
        }
        created.push({ ...upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" }), key: "twitter:card" });

        // JSON-LD: Organization / LocalBusiness
        const orgSchema = {
            "@context": "https://schema.org",
            "@type": "AutoPartsStore",
            "name": name,
            "url": shopAbsoluteUrl(shop.slug),
            "logo": shop.company_logo_url || undefined,
            "telephone": shop.phone_office || undefined,
            "email": shop.contact_email || undefined,
            "address": shop.city ? {
                "@type": "PostalAddress",
                "addressLocality": shop.city,
                "addressCountry": shop.country || "DE",
            } : undefined,
        };
        const { el: orgEl, isNew: orgNew } = injectJsonLd("twowheels-shop-ld-org", orgSchema);
        created.push({ el: orgEl, isNew: orgNew, key: "ld-org" });

        // JSON-LD: Product (Detail) ODER ItemList (Liste)
        if (articleDetail) {
            const productSchema = {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": articleDetail.name,
                "sku": articleDetail.sku,
                "gtin13": articleDetail.ean || undefined,
                "brand": articleDetail.manufacturer ? { "@type": "Brand", "name": articleDetail.manufacturer } : undefined,
                "mpn": articleDetail.manufacturer_part_number || undefined,
                "description": articleDetail.description || undefined,
                "image": (articleDetail.photo_paths || []).map((p) => `${API}/api/shop/photo/${p}`),
                "offers": {
                    "@type": "Offer",
                    "url": `${shopAbsoluteUrl(shop.slug)}/article/${articleDetail.id}`,
                    "priceCurrency": "EUR",
                    "price": articleDetail.price_gross > 0 ? articleDetail.price_gross.toFixed(2) : undefined,
                    "availability": articleDetail.stock_qty > 0 ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
                    "seller": { "@type": "Organization", "name": name },
                },
            };
            const { el: prodEl, isNew: prodNew } = injectJsonLd("twowheels-shop-ld-product", productSchema);
            created.push({ el: prodEl, isNew: prodNew, key: "ld-product" });
        } else if (articles && articles.length) {
            const itemList = {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "name": `Artikel im Shop ${name}`,
                "itemListElement": articles.slice(0, 100).map((a, idx) => ({
                    "@type": "ListItem",
                    "position": idx + 1,
                    "url": `${shopAbsoluteUrl(shop.slug)}/article/${a.id}`,
                    "name": a.name,
                })),
            };
            const { el: listEl, isNew: listNew } = injectJsonLd("twowheels-shop-ld-list", itemList);
            created.push({ el: listEl, isNew: listNew, key: "ld-list" });
        }

        // Cleanup on unmount: nur neu erzeugte Tags entfernen, bestehende behalten
        return () => {
            document.title = prevTitle;
            created.forEach((c) => {
                if (c.isNew && c.el && c.el.parentNode) {
                    c.el.parentNode.removeChild(c.el);
                }
            });
        };
    }, [shop, articles, articleDetail]);
}
